import { inject, Injectable } from '@angular/core';
import { defer, lastValueFrom, map, Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import {
  WorkActivityTypesV2Service,
  WorkArchiveboxesV2Service,
  WorkAudiencesV2Service,
  WorkAuthenticatorsV2Service,
  WorkBusinessdivisionsV2Service,
  WorkBusinessUnitsV2Service,
  WorkCategoriesV2Service,
  WorkCategoryNameEnum,
  WorkContactsV2Service,
  WorkContainertypesV2Service,
  WorkDocumenttypesV2Service,
  WorkEquipmentlogtypesV2Service,
  WorkItemsV2Service,
  WorkLeadCategoriesV2Service,
  WorkLeadSubCategoriesV2Service,
  WorkLeadsubcategory,
  WorkLocationsV2Service,
  WorkMailtypesV2Service,
  WorkMessagingV2Service,
  WorkOutcometypesV2Service,
  WorkPhonetypesV2Service,
  WorkProductgroup,
  WorkProductgroupsV2Service,
  WorkRealEstateTypesV2Service,
  WorkReservationteam,
  WorkReservationteamsV2Service,
  WorkRolesEnum,
  WorkRoletypesV2Service,
  WorkStandardtasksV2Service,
  WorkStatusSelectorEnum,
  WorkStatusV2Service,
  WorkStocklocationsV2Service,
  WorkTagsV2Service,
  WorkTaskgroupsV2Service,
  WorkTasktypesV2Service,
  WorkTeamsV2Service,
  WorkTimeBlockTemplatesV2Service,
  WorkTimerecordtypesV2Service,
  WorkTitlesV2Service,
  WorkUnitsV2Service,
  WorkUsersV2Service,
} from '@work-frontend-monorepo/work-rest-api';
import { SelectDropdownOptions } from '../models/select-dropdown-options.model';
import { DropdownOption } from '../models/dropdown-option.model';

@Injectable({
  providedIn: 'root',
})
export class SelectDropdownOptionsApiService {
  public activityTypes$: Observable<SelectDropdownOptions[]>;
  public allCantonsFromSwitzerland$: Observable<SelectDropdownOptions[]>;
  public archiveBoxes$: Observable<SelectDropdownOptions[]>;
  public audiences$: Observable<SelectDropdownOptions[]>;
  public authenticators$: Observable<SelectDropdownOptions[]>;
  public businessdivisions$: Observable<SelectDropdownOptions[]>;
  public businessunits$: Observable<SelectDropdownOptions[]>;
  public cantons$: Observable<SelectDropdownOptions[]>;
  public categories$: Observable<SelectDropdownOptions[]>;
  public contactProducers$: Observable<SelectDropdownOptions[]>;
  public contactSuppliers$: Observable<SelectDropdownOptions[]>;
  public contactTitles$: Observable<SelectDropdownOptions[]>;
  public containertypes$: Observable<SelectDropdownOptions[]>;
  public documentStatuses$: Observable<SelectDropdownOptions[]>;
  public documenttypes$: Observable<SelectDropdownOptions[]>;
  public documenttypesWithContainertype$: Observable<SelectDropdownOptions[]>;
  public elektriker$: Observable<SelectDropdownOptions[]>;
  public equipmentlogtypes$: Observable<SelectDropdownOptions[]>;
  public extern$: Observable<SelectDropdownOptions[]>;
  public intern$: Observable<SelectDropdownOptions[]>;
  public itemTypes$: Observable<SelectDropdownOptions[]>;
  public kundenberater$: Observable<SelectDropdownOptions[]>;
  public leadCategories$: Observable<SelectDropdownOptions[]>;
  public locations$: Observable<SelectDropdownOptions[]>;
  public mailTypes$: Observable<SelectDropdownOptions[]>;
  public messagingTemplates$: Observable<SelectDropdownOptions[]>;
  public outcomeTypes$: Observable<SelectDropdownOptions[]>;
  public phoneTypes$: Observable<SelectDropdownOptions[]>;
  public productgroups$: Observable<SelectDropdownOptions[]>;
  /**
   * Vollstaendige Warengruppen-Entitaeten (nicht nur Dropdown-Optionen).
   *
   * Wird gebraucht, wo die Konfiguration auf der Warengruppe selbst ausgewertet werden muss
   * (z.B. `visibleItemFields` fuer die Sichtbarkeit von Artikelfeldern).
   */
  public productgroupEntities$: Observable<WorkProductgroup[]>;
  public projectstatus$: Observable<SelectDropdownOptions[]>;
  public projektleiter$: Observable<SelectDropdownOptions[]>;
  public realestatetypes$: Observable<SelectDropdownOptions[]>;
  public reservationteams$: Observable<SelectDropdownOptions[]>;
  public roleTypes$: Observable<SelectDropdownOptions[]>;
  public standardtasks$: Observable<SelectDropdownOptions[]>;
  public stockLocations$: Observable<SelectDropdownOptions[]>;
  public stockLocationsWithoutParents$: Observable<SelectDropdownOptions[]>;
  public tags$: Observable<SelectDropdownOptions[]>;
  public taskgroups$: Observable<SelectDropdownOptions[]>;
  public tasktypes$: Observable<SelectDropdownOptions[]>;
  public teams$: Observable<SelectDropdownOptions[]>;
  public ticketstatus$: Observable<SelectDropdownOptions[]>;
  public timeBlockTemplates$: Observable<SelectDropdownOptions[]>;
  public timerecordtypes$: Observable<SelectDropdownOptions[]>;
  public units$: Observable<SelectDropdownOptions[]>;

  /**
   * Service injects.
   */
  private readonly authenticatorsV2Service = inject(WorkAuthenticatorsV2Service);
  private readonly unitsV2Service = inject(WorkUnitsV2Service);
  private readonly usersV2Service = inject(WorkUsersV2Service);
  private readonly workActivityTypesV2Service = inject(WorkActivityTypesV2Service);
  private readonly workArchiveboxesV2Service = inject(WorkArchiveboxesV2Service);
  private readonly workAudiencesV2Service = inject(WorkAudiencesV2Service);
  private readonly workBusinessdivisionsV2Service = inject(WorkBusinessdivisionsV2Service);
  private readonly workBusinessUnitsV2Service = inject(WorkBusinessUnitsV2Service);
  private readonly workCategoriesV2Service = inject(WorkCategoriesV2Service);
  private readonly workContactsV2Service = inject(WorkContactsV2Service);
  private readonly workContainertypesV2Service = inject(WorkContainertypesV2Service);
  private readonly workDocumenttypesV2Service = inject(WorkDocumenttypesV2Service);
  private readonly workEquipmentlogtypesV2Service = inject(WorkEquipmentlogtypesV2Service);
  private readonly workItemsV2Service = inject(WorkItemsV2Service);
  private readonly workLeadCategoriesV2Service = inject(WorkLeadCategoriesV2Service);
  private readonly workLeadSubCategoriesV2Service = inject(WorkLeadSubCategoriesV2Service);
  private readonly workLocationsV2Service = inject(WorkLocationsV2Service);
  private readonly workMailtypesV2Service = inject(WorkMailtypesV2Service);
  private readonly workMessagingV2Service = inject(WorkMessagingV2Service);
  private readonly workOutcometypesV2Service = inject(WorkOutcometypesV2Service);
  private readonly workPhonetypesV2Service = inject(WorkPhonetypesV2Service);
  private readonly workProductgroupsV2Service = inject(WorkProductgroupsV2Service);
  private readonly workRealEstateTypesV2Service = inject(WorkRealEstateTypesV2Service);
  private readonly workReservationteamsV2Service = inject(WorkReservationteamsV2Service);
  private readonly workRoleTypesV2Service = inject(WorkRoletypesV2Service);
  private readonly workStandardtasksV2Service = inject(WorkStandardtasksV2Service);
  private readonly workStatusV2Service = inject(WorkStatusV2Service);
  private readonly workStocklocationsV2Service = inject(WorkStocklocationsV2Service);
  private readonly workTagsV2Service = inject(WorkTagsV2Service);
  private readonly workTaskgroupsV2Service = inject(WorkTaskgroupsV2Service);
  private readonly workTasktypesV2Service = inject(WorkTasktypesV2Service);
  private readonly workTeamsV2Service = inject(WorkTeamsV2Service);
  private readonly workTimeBlockTemplatesV2Service = inject(WorkTimeBlockTemplatesV2Service);
  private readonly workTimerecordtypesV2Service = inject(WorkTimerecordtypesV2Service);
  private readonly workTitlesV2Service = inject(WorkTitlesV2Service);

  constructor() {
    this.activityTypes$ = defer(() => this.workActivityTypesV2Service.activitytypesV2ControllerGetAllDropdownOptions()).pipe(
      shareReplay(1),
    );
    this.archiveBoxes$ = defer(() => this.workArchiveboxesV2Service.archiveboxesV2ControllerGetAllDropdownOptions()).pipe(shareReplay(1));
    this.audiences$ = defer(() => this.workAudiencesV2Service.audiencesV2ControllerGetAllDropdownOptions()).pipe(shareReplay(1));
    this.authenticators$ = defer(() => this.authenticatorsV2Service.authenticatorsV2ControllerGetAllDropdownOptions()).pipe(shareReplay(1));
    this.equipmentlogtypes$ = defer(() => this.workEquipmentlogtypesV2Service.equipmentlogtypesV2ControllerGetAllDropdownOptions()).pipe(
      shareReplay(1),
    );
    this.elektriker$ = defer(() =>
      this.usersV2Service.usersV2ControllerGetAllDropdownOptions({ roles: [WorkRolesEnum.AxElektriker].join(',') }),
    ).pipe(shareReplay(1));
    this.extern$ = defer(() =>
      this.usersV2Service.usersV2ControllerGetAllDropdownOptions({ roles: [WorkRolesEnum.AxExtern].join(',') }),
    ).pipe(shareReplay(1));
    this.intern$ = defer(() =>
      this.usersV2Service.usersV2ControllerGetAllDropdownOptions({ roles: [WorkRolesEnum.AxIntern].join(',') }),
    ).pipe(shareReplay(1));
    this.kundenberater$ = defer(() =>
      this.usersV2Service.usersV2ControllerGetAllDropdownOptions({ roles: [WorkRolesEnum.AxKundenberater].join(',') }),
    ).pipe(shareReplay(1));
    this.projektleiter$ = defer(() =>
      this.usersV2Service.usersV2ControllerGetAllDropdownOptions({ roles: [WorkRolesEnum.AxProjektleiter].join(',') }),
    ).pipe(shareReplay(1));
    this.businessdivisions$ = defer(() => this.workBusinessdivisionsV2Service.businessdivisionsV2ControllerGetAllDropdownOptions()).pipe(
      shareReplay(1),
    );
    this.businessunits$ = defer(() => this.workBusinessUnitsV2Service.businessunitsV2ControllerGetAllDropdownOptions()).pipe(
      shareReplay(1),
    );
    this.containertypes$ = defer(() => this.workContainertypesV2Service.containertypesV2ControllerGetAllDropdownOptions()).pipe(
      shareReplay(1),
    );
    this.contactSuppliers$ = defer(() =>
      this.workContactsV2Service.contactsV2ControllerGetContactsDropdownOptions({ contactType: WorkCategoryNameEnum.Lieferanten }),
    ).pipe(shareReplay(1));
    this.contactProducers$ = defer(() =>
      this.workContactsV2Service.contactsV2ControllerGetContactsDropdownOptions({ contactType: WorkCategoryNameEnum.Hersteller }),
    ).pipe(shareReplay(1));
    this.cantons$ = defer(() => this.workLocationsV2Service.locationsV2ControllerGetAllDropdownOptionsCantons()).pipe(shareReplay(1));
    this.allCantonsFromSwitzerland$ = defer(() =>
      this.workLocationsV2Service.locationsV2ControllerGetAllDropdownOptionsCantonsFromSwitzerland(),
    ).pipe(shareReplay(1));
    this.categories$ = defer(() => this.workCategoriesV2Service.categoriesV2ControllerGetAllDropdownOptions()).pipe(shareReplay(1));
    this.contactTitles$ = defer(() => this.workTitlesV2Service.titlesV2ControllerGetAllDropdownOptions()).pipe(shareReplay(1));
    this.documenttypes$ = defer(() => this.workDocumenttypesV2Service.documenttypesV2ControllerGetAllDropdownOptions()).pipe(
      shareReplay(1),
    );
    this.documenttypesWithContainertype$ = defer(() =>
      this.workDocumenttypesV2Service.documenttypesV2ControllerGetAllDropdownOptions({ withContainerTypeNames: true }),
    ).pipe(shareReplay(1));
    this.leadCategories$ = defer(() => this.workLeadCategoriesV2Service.leadcategoriesV2ControllerGetAllDropdownOptions()).pipe(
      shareReplay(1),
    );
    this.locations$ = defer(() => this.workLocationsV2Service.locationsV2ControllerGetAllDropdownOptions()).pipe(shareReplay(1));
    this.mailTypes$ = defer(() => this.workMailtypesV2Service.mailtypesV2ControllerGetAllDropdownOptions()).pipe(shareReplay(1));
    this.messagingTemplates$ = defer(() => this.workMessagingV2Service.messagingV2ControllerGetAllMessagingTemplateDropdownOptions()).pipe(
      shareReplay(1),
    );
    this.outcomeTypes$ = defer(() => this.workOutcometypesV2Service.outcometypesV2ControllerGetAllDropdownOptions()).pipe(shareReplay(1));
    this.phoneTypes$ = defer(() => this.workPhonetypesV2Service.phonetypesV2ControllerGetAllDropdownOptions()).pipe(shareReplay(1));
    this.realestatetypes$ = defer(() => this.workRealEstateTypesV2Service.realestatetypesV2ControllerGetAllDropdownOptions()).pipe(
      shareReplay(1),
    );
    this.reservationteams$ = defer(() => this.workReservationteamsV2Service.reservationteamsV2ControllerGetAllDropdownOptions()).pipe(
      shareReplay(1),
    );
    this.roleTypes$ = defer(() => this.workRoleTypesV2Service.roletypesV2ControllerGetAllDropdownOptions()).pipe(shareReplay(1));
    this.standardtasks$ = defer(() => this.workStandardtasksV2Service.standardtasksV2ControllerGetAllDropdownOptions()).pipe(
      shareReplay(1),
    );
    this.stockLocations$ = defer(() => this.workStocklocationsV2Service.stocklocationsV2ControllerGetAllDropdownOptions()).pipe(
      shareReplay(1),
    );
    this.stockLocationsWithoutParents$ = defer(() =>
      this.workStocklocationsV2Service.stocklocationsV2ControllerGetAllDropdownOptions({ withoutParents: true }),
    ).pipe(shareReplay(1));
    this.tags$ = defer(() => this.workTagsV2Service.tagsV2ControllerGetAllDropdownOptions()).pipe(shareReplay(1));
    this.tasktypes$ = defer(() => this.workTasktypesV2Service.tasktypesV2ControllerGetAllDropdownOptions()).pipe(shareReplay(1));
    this.taskgroups$ = defer(() => this.workTaskgroupsV2Service.taskgroupsV2ControllerGetAllDropdownOptions()).pipe(shareReplay(1));
    this.teams$ = defer(() => this.workTeamsV2Service.teamsV2ControllerGetAllDropdownOptions()).pipe(shareReplay(1));
    this.timerecordtypes$ = defer(() => this.workTimerecordtypesV2Service.timerecordtypesV2ControllerGetAllDropdownOptions()).pipe(
      shareReplay(1),
    );
    this.units$ = defer(() => this.unitsV2Service.unitsV2ControllerGetAllDropdownOptions()).pipe(shareReplay(1));
    this.timeBlockTemplates$ = defer(() => this.workTimeBlockTemplatesV2Service.timeBlockTemplatesV2ControllerGetAllDropdownOptions()).pipe(
      shareReplay(1),
    );
    this.itemTypes$ = defer(() => this.workItemsV2Service.itemsV2ControllerGetDropdownOptionsTypes()).pipe(shareReplay(1));
    this.productgroups$ = defer(() => this.workProductgroupsV2Service.productgroupsV2ControllerGetAllDropdownOptions()).pipe(
      shareReplay(1),
    );
    this.productgroupEntities$ = defer(() =>
      this.workProductgroupsV2Service.productgroupsV2ControllerFindAllPaginated({ limit: 100 }),
    ).pipe(
      map((paginatedResult) => paginatedResult.data ?? []),
      shareReplay(1),
    );
    this.ticketstatus$ = defer(() =>
      this.workStatusV2Service.statusV2ControllerGetAllBySelector({ selector: WorkStatusSelectorEnum.Tickets }),
    ).pipe(
      shareReplay(1),
      map((statusArray) => {
        return this.mapResultsToDropdownOptions(statusArray);
      }),
    );
    this.projectstatus$ = defer(() =>
      this.workStatusV2Service.statusV2ControllerGetAllBySelector({ selector: WorkStatusSelectorEnum.Projects }),
    ).pipe(
      shareReplay(1),
      map((statusArray) => {
        return this.mapResultsToDropdownOptions(statusArray);
      }),
    );
    this.documentStatuses$ = defer(() =>
      this.workStatusV2Service.statusV2ControllerGetAllBySelector({ selector: WorkStatusSelectorEnum.Documents }),
    ).pipe(
      shareReplay(1),
      map((statusArray) => {
        return this.mapResultsToDropdownOptions(statusArray);
      }),
    );
  }

  public async getLeadSubcategoriesForLeadcategory(leadcategoryId: number): Promise<DropdownOption[]> {
    try {
      const leadsubcategories = await lastValueFrom(
        this.workLeadSubCategoriesV2Service.leadsubcategoriesV2ControllerFindAllPaginated({
          'filter.leadcategoryId': [`$eq:${leadcategoryId}`],
        }),
      );
      return leadsubcategories.data.map((entry: WorkLeadsubcategory) => ({
        label: `${entry.name}`,
        value: entry.id,
      }));
    } catch (nothingFound) {
      return [];
    }
  }

  private mapResultsToDropdownOptions(results: any[]): SelectDropdownOptions[] {
    return results.map((result) => ({
      label: result.name || '',
      value: result.id,
    }));
  }

  private mapReservationteamToDropdownOption(team: WorkReservationteam): SelectDropdownOptions {
    let label = `${team.id}`;
    if (team.businessunit && team.containertypes && team.containertypes.length) {
      label = `${team.businessunit.shortname}-${team.containertypes.map((ct) => ct.shortname).join('-')}`;
    }
    return { label, value: team.id };
  }
}
